import { useMemo, useCallback } from "react";
import useAuth from "./useAuth";
import { useHasActiveSubscription } from "./useHasActiveSubscription";
import { useHasInternalNewsquawkUserRole } from "./useHasInternalNewsquawkUserRole";
import { useGetAppRoles } from "./useGetAppRoles";
import { useHasBetaWidgetsRole } from "./useHasBetaWidgetsRole";
import { useAccessExpiresAtEpoch } from "./useAccessExpiresAtEpoch";
import {
  useSubscriptionQuery,
  type SubscriptionQuery,
} from "./subscription/useSubscriptionQuery";
import { Widget } from "@/utils/widgets";
import Keycloak from "keycloak-js";

export interface AccessControl {
  // Auth primitives
  authenticated: boolean;
  keycloakInstance: Keycloak | null;

  // Role booleans
  isInternalUser: boolean;
  isSubscriber: boolean;
  /** Member of beta-widgets-preview-group — sees `betaPreview` widgets */
  isBetaPreviewUser: boolean;
  roles: string[];

  // Subscription query (full React Query result)
  subscriptionQuery: SubscriptionQuery;

  // Token-based access epoch
  /** Date derived from the KC token's access_expires_at_epoch (null if field absent) */
  accessExpiresAtDate: Date | null;

  // Derived access states
  /** User's KC access_expires_at_epoch is in the past */
  isAccessExpired: boolean;
  /** User has no live access: unauthenticated OR expired OR (not internal AND not subscriber) */
  isInDemoMode: boolean;
  /** User has live access: authenticated AND (internal OR subscriber) */
  hasLiveAccess: boolean;
  /** Show red banner: authenticated AND not internal AND not subscriber */
  showSubscriptionBanner: boolean;

  // Access check functions
  /** Check whether the user can access a specific widget */
  hasAccessToWidget: (widget: Widget) => boolean;
  /** Check whether a squawk channel is role-locked for this user */
  isChannelRoleLocked: (requiredRole: string | undefined) => boolean;
}

export const useAccessControl = (): AccessControl => {
  const { authenticated, keycloakInstance } = useAuth();
  const isInternalUser = useHasInternalNewsquawkUserRole();
  const isSubscriber = useHasActiveSubscription();
  const isBetaPreviewUser = useHasBetaWidgetsRole();
  const roles = useGetAppRoles();
  const { expiresAtDate: accessExpiresAtDate, isExpired: isAccessExpired } =
    useAccessExpiresAtEpoch();
  const subscriptionQuery = useSubscriptionQuery();

  const isInDemoMode = useMemo(
    () =>
      !authenticated ||
      isAccessExpired ||
      (!isInternalUser && !isSubscriber),
    [authenticated, isAccessExpired, isInternalUser, isSubscriber]
  );

  const hasLiveAccess = useMemo(
    () =>
      authenticated &&
      !isAccessExpired &&
      (isInternalUser || isSubscriber),
    [authenticated, isAccessExpired, isInternalUser, isSubscriber]
  );

  const showSubscriptionBanner = useMemo(
    () =>
      authenticated &&
      !isInternalUser &&
      (!isSubscriber || isAccessExpired),
    [authenticated, isInternalUser, isSubscriber, isAccessExpired]
  );

  const hasAccessToWidget = useCallback(
    (widget: Widget): boolean => {
      if (isInternalUser) return true;
      // Beta preview is tier-independent — checked before the subscriber gate so
      // non-subscribing publishers get in, and everyone else gets a hard false
      // rather than falling through to the upgrade-prompt path.
      if (widget.betaPreview) return isBetaPreviewUser;
      if (widget.unAuthAccessible) return true;
      if (!isSubscriber || isAccessExpired) return false;
      if (!widget.accessGroup) return true;
      return roles.includes(widget.accessGroup) && !widget.internalOnly;
    },
    [isInternalUser, isBetaPreviewUser, isSubscriber, isAccessExpired, roles]
  );

  const isChannelRoleLocked = useCallback(
    (requiredRole: string | undefined): boolean => {
      if (!requiredRole) return false;
      if (!authenticated) return false;
      if (isInternalUser) return false;
      if (!isSubscriber || isAccessExpired) return false;
      return !roles.includes(requiredRole);
    },
    [authenticated, isInternalUser, isSubscriber, isAccessExpired, roles]
  );

  return useMemo(
    () => ({
      authenticated,
      keycloakInstance,
      isInternalUser,
      isSubscriber,
      isBetaPreviewUser,
      roles,
      subscriptionQuery,
      accessExpiresAtDate,
      isAccessExpired,
      isInDemoMode,
      hasLiveAccess,
      showSubscriptionBanner,
      hasAccessToWidget,
      isChannelRoleLocked,
    }),
    [
      authenticated,
      keycloakInstance,
      isInternalUser,
      isSubscriber,
      isBetaPreviewUser,
      roles,
      subscriptionQuery,
      accessExpiresAtDate,
      isAccessExpired,
      isInDemoMode,
      hasLiveAccess,
      showSubscriptionBanner,
      hasAccessToWidget,
      isChannelRoleLocked,
    ]
  );
};
